import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  AmenitiesList as BaseAmenitiesList,
  contentSchema as baseSchema,
} from './_AmenitiesListBase';

/**
 * Weidner Via6 AmenitiesList override.
 *
 * Renders the inherited rise AmenitiesList unchanged, but applies two
 * presentational tweaks via a wrapping scope:
 *
 * 1. Recolors the outermost <section> background from the theme's
 *    neutral `--vibe-color-background` to the brand primary navy
 *    (`--vibe-color-primary`). The rise component hard-codes its bg
 *    via a Tailwind arbitrary class (`bg-[var(--vibe-color-background)]`)
 *    and does not expose a className prop, so we override it from a
 *    wrapping scope with a slightly more specific selector (no
 *    !important needed — a parent attribute + child type selector
 *    beats a single utility class on specificity).
 *
 * 2. Flips the top row (Community / `.amenities-row--img-left`) so the
 *    image sits on the right and the list on the left, the opposite of
 *    the rise default. The base component renders the panels in
 *    source order inside a CSS grid, so reordering with CSS `order` is
 *    enough — no JSX changes needed. We scope the flip to ≥769px
 *    because the base component already forces image-on-top below
 *    768px and we want to preserve that mobile behavior.
 *
 * 3. Recolors the list panel (`.amenities-row-content`) from white to
 *    the brand primary navy and switches the heading, list items,
 *    bullet, and pagination controls to light/`text-on-primary` so
 *    they stay legible. The image panel is unaffected because the
 *    image itself fully covers its grid cell.
 *
 * 4. Restyles the amenity list itself from a two-column bulleted list
 *    into a single-column underlined list: bullets are hidden, the
 *    grid drops to one column, and each item gets a bottom border that
 *    spans the full column width. Both the visible list and the
 *    hidden measurement list use the same `.amenities-col-list` class,
 *    so the rise component's pagination measurement still works — it
 *    just paginates based on the (taller) single-column layout.
 *
 * 5. Re-themes both column headings:
 *      - "Community Amenities" → "Community amenities" (lowercased
 *        second word) with "Community" in the brand cursive accent.
 *      - "Interior Luxuries" → "Interior amenities." (renamed + trailing
 *        period) with "Interior" in the brand cursive accent.
 *    The rise component hardcodes both defaults in its JSX, so we
 *    can't change them from a contentSchema override, and a full
 *    fork for two strings + one cursive word each would be a 750-line
 *    maintenance burden. Instead we lazy-load the Satisfy Google Font,
 *    inject the `.vibe-cursive-accent` rule used elsewhere in this
 *    theme, and a post-mount effect rewrites the inner HTML of each
 *    `.amenities-col-heading` whose plain-text content still matches
 *    the parent default. The effect is idempotent (skips headings
 *    that already contain a `.vibe-cursive-accent` span) so an editor
 *    override that authors their own markup — or our own previous
 *    pass — is left alone.
 *
 * The contentSchema is the parent's schema re-exported so editor
 * affordances (community/apartment headings + collection bindings)
 * still attach to this wrapper function identity.
 */
export function AmenitiesList() {
  // Lazy-load the "Satisfy" cursive Google Font used by the
  // .vibe-cursive-accent span. De-duped against PropertyOverview /
  // HomeMap / FloorplansList / AmenitiesGrid / InteriorIntro / Header
  // by the data-vibe-font marker so we never inject the stylesheet
  // link more than once.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  // After BaseAmenitiesList mounts (and on any heading content change —
  // e.g. when an editor saves an override), rewrite each column heading
  // whose plain-text content still matches the rise default. The
  // rewrite both renames the heading (only the Interior one — Community
  // keeps its name but lowercases "Amenities") and wraps the leading
  // word in a `<span class="vibe-cursive-accent">` so the brand's
  // cursive treatment applies. Idempotent: bails if the heading already
  // contains a `.vibe-cursive-accent` span, so an editor-authored
  // override (or our own previous pass) is left alone. We re-scan on
  // subtree mutations because the rise component re-renders the
  // heading when the editor saves.
  //
  // We mutate the inner `<span data-vibesite-richtext>` (RichTextContent's
  // hydration-safe wrapper) rather than the heading itself, so the
  // surrounding EditableToken wrappers stay intact.
  useEffect(() => {
    const root = document.querySelector('[data-weidner-amenities-list-bg]');
    if (!root) return;

    // Map of rise-default plain text → themed inner HTML. Listed
    // here (rather than inline below) so the mapping reads at a glance
    // and is easy to extend.
    const REWRITES: Array<{ match: string; html: string }> = [
      {
        match: 'Community Amenities',
        html: '<span class="vibe-cursive-accent">Community</span> amenities',
      },
      {
        match: 'Interior Luxuries',
        html: '<span class="vibe-cursive-accent">Interior</span> amenities.',
      },
    ];

    const applyRewrites = () => {
      const headings = root.querySelectorAll<HTMLElement>('.amenities-col-heading');
      headings.forEach((heading) => {
        // Idempotent / editor-safe: skip if our cursive span (or an
        // editor-authored one) is already present.
        if (heading.querySelector('.vibe-cursive-accent')) return;

        const text = (heading.textContent || '').trim();
        const rewrite = REWRITES.find((r) => r.match === text);
        if (!rewrite) return;

        // RichTextContent renders content inside a
        // <span data-vibesite-richtext>. Fall back to the heading
        // itself if (somehow) the inner wrapper isn't there.
        const inner = heading.querySelector('[data-vibesite-richtext]') ?? heading;
        inner.innerHTML = rewrite.html;
      });
    };

    applyRewrites();
    const observer = new MutationObserver(applyRewrites);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div data-weidner-amenities-list-bg>
      <BaseAmenitiesList />
      <style>{`
        [data-weidner-amenities-list-bg] > section {
          background-color: var(--vibe-color-primary);
        }

        /* Cursive accent rule for the <span class="vibe-cursive-accent">
           wrapped around the leading word of the community heading
           (injected by the post-mount effect above). text-transform:
           none escapes any uppercase utility on the heading so the
           script glyphs render in their natural mixed case. Mirrors
           the same treatment used in PropertyOverview / AmenitiesGrid /
           FloorplansList / HomeMap / InteriorIntro headings. */
        [data-weidner-amenities-list-bg] .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }

        /* Flip the top (Community) row so image is on the right and the
           list is on the left. Desktop/tablet only — mobile keeps the
           base behavior of image-on-top. */
        @media (min-width: 769px) {
          [data-weidner-amenities-list-bg] .amenities-row--img-left > .amenities-row-image {
            order: 2;
          }
          [data-weidner-amenities-list-bg] .amenities-row--img-left > .amenities-row-content {
            order: 1;
          }
        }

        /* Make the list panel match the section bg (primary navy) and
           switch text/UI to light variants for contrast. We paint the
           bg on .amenities-row-content (just the list side) rather than
           the whole .amenities-row, so the image panel is untouched. */
        [data-weidner-amenities-list-bg] .amenities-row-content {
          background-color: var(--vibe-color-primary);
        }
        [data-weidner-amenities-list-bg] .amenities-col-heading,
        [data-weidner-amenities-list-bg] .amenities-col-item {
          color: var(--vibe-color-text-on-primary);
        }
        /* Bump the column heading a bit larger than the rise default
           so it carries more weight against the navy panel and the
           cursive accent reads more comfortably. Scaled at each
           breakpoint to keep the same proportional jump the base
           component already defines (rise: 2 / 1.5 / 1.15rem). */
        [data-weidner-amenities-list-bg] .amenities-col-heading {
          font-size: 2.5rem;
        }
        @media (max-width: 900px) {
          [data-weidner-amenities-list-bg] .amenities-col-heading {
            font-size: 1.875rem;
          }
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-list-bg] .amenities-col-heading {
            font-size: 1.4rem;
          }
        }
        [data-weidner-amenities-list-bg] .amenities-col-bullet {
          background: var(--vibe-color-text-on-primary);
        }
        [data-weidner-amenities-list-bg] .amenities-pagination {
          color: var(--vibe-color-text-on-primary);
        }
        /* Pagination buttons sit on the navy bg, so flip them to a
           translucent-white pill that turns solid white on hover. */
        [data-weidner-amenities-list-bg] .amenities-pagination-btn {
          background: transparent;
          border-color: rgba(255, 255, 255, 0.35);
          color: var(--vibe-color-text-on-primary);
        }
        [data-weidner-amenities-list-bg] .amenities-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-text-on-primary);
          color: var(--vibe-color-primary);
        }

        /* Single-column underlined list.
           - Collapse the rise two-column grid to one column.
           - Hide the bullet (and let it not consume flex space).
           - Each item gets a bottom border spanning the column width,
             which is the li's full width since the li is a flex parent
             stretched by the grid. Padding gives the underline breathing
             room. */
        [data-weidner-amenities-list-bg] .amenities-col-list {
          grid-template-columns: 1fr;
          gap: 0;
        }
        [data-weidner-amenities-list-bg] .amenities-col-bullet {
          display: none;
        }
        [data-weidner-amenities-list-bg] .amenities-col-item {
          padding: 0.55rem 0;
          border-bottom: 1px solid var(--vibe-color-text-on-primary);
        }
        @media (max-width: 600px) {
          [data-weidner-amenities-list-bg] .amenities-col-item {
            padding: 0.4rem 0;
          }
        }
      `}</style>
    </div>
  );
}

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  baseSchema;

export default AmenitiesList;
